import React, { useState } from "react";
import { Card, Typography, Space, Descriptions, Button, message, Alert, Tag } from "antd";
import { FileText, Download, CheckCircle2, Copy } from "lucide-react";

const { Title, Text, Paragraph } = Typography;

interface Step3Props {
  experimentData?: any;
  powerResults?: any;
}

const Step3ReviewAndExport: React.FC<Step3Props> = ({ experimentData, powerResults }) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);

    // Simulate export delay
    setTimeout(() => {
      // Create a summary report
      const report = {
        experimentName: experimentData?.experimentName,
        experimentType: experimentData?.experimentType,
        configuration: {
          significanceLevel: experimentData?.significanceLevel,
          statisticalPower: experimentData?.statisticalPower,
          baselineConversionRate: experimentData?.baselineConversionRate,
          minimumDetectableEffect: experimentData?.minimumDetectableEffect,
          testDuration: experimentData?.testDuration,
        },
        results: powerResults,
        timestamp: new Date().toISOString(),
      };

      // Convert to JSON and download
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `power-calculation-${experimentData?.experimentName?.replace(/\s+/g, "-")}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setExporting(false);
      message.success("Power calculation report exported successfully!");
    }, 1000);
  };

  const handleCopyToClipboard = () => {
    const summary = `
Power Calculation Summary
========================
Experiment: ${experimentData?.experimentName}

Budget Information:
- Campaign Budget: $${experimentData?.campaignBudget?.toLocaleString()}
- BAU Budget: $${experimentData?.bauBudget?.toLocaleString()}
- Incremental Budget: $${powerResults?.incrementalBudget?.toLocaleString()}

Cost & Conversion Targets:
- Target CPIC: $${experimentData?.targetCPIC?.toLocaleString()}
- Expected Incremental Conversions: ${powerResults?.expectedIncrementalConversions?.toLocaleString()}
- Minimum Detectable Effect: ${powerResults?.assumedMDE?.toFixed(1)}% lift

Investment Requirements:
- Minimum Investment Per Variant: $${powerResults?.minimumInvestment?.toLocaleString()}
- Total Sample Size: ${powerResults?.totalSampleSize?.toLocaleString()}
- Sample Size Per Variant: ${powerResults?.sampleSizePerVariant?.toLocaleString()}

Experiment Design:
- Ideal Test Duration: ${powerResults?.expectedDuration} days (${Math.ceil(powerResults?.expectedDuration / 7)} weeks)
- Daily Traffic Required: ${powerResults?.dailyTraffic?.toLocaleString()}

Statistical Parameters:
- Statistical Power: ${experimentData?.statisticalPower}
- Significance Level: 0.05 (5%)

Historical Data: ${experimentData?.fileList?.[0]?.name || "Uploaded"}
    `.trim();

    navigator.clipboard.writeText(summary);
    message.success("Summary copied to clipboard!");
  };


  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-8 h-8 text-blue-600" />
          <Title level={2} className="mb-0">Review & Export</Title>
        </div>
        <Text type="secondary" className="text-base">
          Review your power calculation and export the results
        </Text>
      </div>

      <Space direction="vertical" size="large" className="w-full">
        <Alert
          message="Calculation Complete"
          description="Your power calculation is ready. Review the details below and export the report for your records."
          type="success"
          showIcon
          icon={<CheckCircle2 className="w-5 h-5" />}
        />

        {/* Experiment Configuration */}
        <Card
          title={<span className="font-semibold text-lg">Experiment Configuration</span>}
          className="shadow-sm"
        >
          <Descriptions column={{ xs: 1, sm: 2 }} bordered>
            <Descriptions.Item label="Experiment Name">
              <strong>{experimentData?.experimentName}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Campaign Budget">
              <Tag color="green">${experimentData?.campaignBudget?.toLocaleString()}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="BAU Budget">
              ${experimentData?.bauBudget?.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Incremental Budget">
              <strong>${powerResults?.incrementalBudget?.toLocaleString()}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Statistical Power (1 - β)">
              {experimentData?.statisticalPower}
            </Descriptions.Item>
            <Descriptions.Item label="Target CPIC">
              ${experimentData?.targetCPIC?.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Expected Incremental Conversions">
              {powerResults?.expectedIncrementalConversions?.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Minimum Detectable Effect">
              {powerResults?.assumedMDE?.toFixed(1)}% lift (calculated)
            </Descriptions.Item>
            <Descriptions.Item label="Historical Data">
              <Tag color="blue">{experimentData?.fileList?.[0]?.name || "Uploaded"}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Sample Size Results */}
        <Card
          title={<span className="font-semibold text-lg">Experiment Design Results</span>}
          className="shadow-sm"
        >
          <Descriptions column={{ xs: 1, sm: 2 }} bordered>
            <Descriptions.Item label="Sample Size Per Variant">
              <strong className="text-blue-600 text-lg">
                {powerResults?.sampleSizePerVariant?.toLocaleString()}
              </strong>
            </Descriptions.Item>
            <Descriptions.Item label="Total Sample Size">
              <strong className="text-purple-600 text-lg">
                {powerResults?.totalSampleSize?.toLocaleString()}
              </strong>
            </Descriptions.Item>
            <Descriptions.Item label="Minimum Investment Required">
              <strong className="text-green-600 text-lg">
                ${powerResults?.minimumInvestment?.toLocaleString()}
              </strong>
            </Descriptions.Item>
            <Descriptions.Item label="Ideal Test Duration">
              <strong className="text-amber-600 text-lg">
                {powerResults?.expectedDuration} days
              </strong>
            </Descriptions.Item>
            <Descriptions.Item label="Daily Traffic Required">
              <strong className="text-green-600 text-lg">
                {powerResults?.dailyTraffic?.toLocaleString()}
              </strong>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Recommendations */}
        <Card
          title={<span className="font-semibold text-lg">Recommendations</span>}
          className="shadow-sm"
        >
          <Space direction="vertical" size="middle" className="w-full">
            <Alert
              message="Budget Allocation"
              description={`Allocate a minimum of $${powerResults?.minimumInvestment?.toLocaleString()} per test variant. Your total incremental budget of $${powerResults?.incrementalBudget?.toLocaleString()} supports this experiment design.`}
              type="success"
              showIcon
            />
            <Alert
              message="Test & Control Markets"
              description={`Based on your historical data, you need ${powerResults?.sampleSizePerVariant?.toLocaleString()} locations per variant. Review your uploaded data to identify optimal test and control markets.`}
              type="info"
              showIcon
            />
            <Alert
              message="Experiment Duration"
              description={`Run your experiment for the full ${powerResults?.expectedDuration} days (approximately ${Math.ceil(powerResults?.expectedDuration / 7)} weeks) to achieve statistical significance. Early stopping may lead to unreliable conclusions.`}
              type="warning"
              showIcon
            />
            <Alert
              message="National vs Regional"
              description={`With a campaign budget of $${experimentData?.campaignBudget?.toLocaleString()}, consider whether national or regional testing better fits your investment level and desired market coverage.`}
              type="info"
              showIcon
            />
          </Space>
        </Card>

        {/* Next Steps */}
        <Card
          title={<span className="font-semibold text-lg">Next Steps</span>}
          className="shadow-sm bg-gradient-to-br from-blue-50 to-purple-50"
        >
          <Paragraph>
            <ol className="list-decimal ml-4 space-y-2">
              <li>Export this power calculation report for stakeholder review and budget approval</li>
              <li>Use the historical data to identify optimal test and control markets</li>
              <li>Allocate the minimum investment of ${powerResults?.minimumInvestment?.toLocaleString()} per test variant</li>
              <li>Set up your geo-experiment with {powerResults?.expectedDuration} days duration</li>
              <li>Monitor progress daily, but complete the full test duration before drawing conclusions</li>
              <li>Analyze results using the Measurement module to calculate ROI and incremental lift</li>
            </ol>
          </Paragraph>
        </Card>

        {/* Export Actions */}
        <Card className="shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <div>
              <Title level={5}>Export Options</Title>
              <Text type="secondary">
                Save your power calculation for future reference and sharing
              </Text>
            </div>
            <Space wrap>
              <Button
                type="primary"
                size="large"
                icon={<Download className="w-4 h-4" />}
                onClick={handleExport}
                loading={exporting}
                className="bg-gradient-to-r from-blue-600 to-purple-600 border-none"
              >
                Export as JSON
              </Button>
              <Button
                size="large"
                icon={<Copy className="w-4 h-4" />}
                onClick={handleCopyToClipboard}
              >
                Copy Summary
              </Button>
            </Space>
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default Step3ReviewAndExport;
